import { Formik, FormikHelpers } from "formik";
import { Dispatch, FC, SetStateAction, createContext, useContext, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { AppContext } from "../appContext/AppContext";
import ComponentPrint from "../component/templateComponent/ComponentPrint";
import { phieuTiepNhan } from "../component/templateComponent/conponents/PhieuTiepNhan";
import { convertDataTiepNhan } from "../component/templateComponent/conponents/Utils";
import { CODE, KEY_DS_TAB_TIEP_NHAN, REGEX, VARIABLE_STRING } from "../utils/Constant";
import { formatDateDTO, validateNgay } from "../utils/FormatUtils";
import { localStorageItem } from "../utils/LocalStorage";
import './PhanHeTiepNhan.scss';
import { CODE_DOI_TUONG, danhSachMenu, initialValuesTiepDon } from "./constants/PhanHeTiepNhan";
import { addBenhNhan, updateBenhNhan } from "./services/TiepNhanServices";
import TiepNhan from "./tab-tiep-nhan/TiepNhan";
import { ITiepDon } from "./models/TiepDonModel";
import { DEFAULT_CODE_SELECT_TIEPDON } from "./constants/constants";
import { KEY_LOCALSTORAGE } from "../auth/core/_consts";
import { daySchema, monthSchema, yearSchema } from "../utils/ValidationSchemaUtils";

interface PhanHeTiepNhanContextProps {
    DSDichVu: any[];
    thongTinBenhNhan: {
        isChangeThongTinBenhNhan?: boolean
        isExamined?: boolean
        isMedicalExamSvcPaid?: boolean
    };
    setThongTinBenhNhan: (value: any) => void;
    handleAddTab: (eventKey: string) => void;
    setDSDichVu: Dispatch<SetStateAction<any[]>>;
    isPrint?: boolean;
    setIsPrint?: (isPrint: boolean) => void;
    isPay: { boolean: boolean, page: number, pageSize: number};
    setIsPay: (isPay: { boolean: boolean, page: number, pageSize: number; }) => void;
    orderMain: object | null;
    setOrderMain: Dispatch<SetStateAction<any>>;
    convertDataBenhNhan: (data: any) => ITiepDon
}

const initialContext = {
    thongTinBenhNhan: {} ,
    setThongTinBenhNhan: () => { },
    handleAddTab: () => { },
    setDSDichVu: () => { },
    DSDichVu: [],
    isPrint: false,
    isPay: {boolean: false, page: 0, pageSize: 0},
    setIsPay: () => { },
    orderMain: null,
    setOrderMain: () => { },
    convertDataBenhNhan: () => ({} as ITiepDon),
}

export const PhanHeTiepNhanContext = createContext<PhanHeTiepNhanContextProps>(initialContext)

export const PhanHeTiepNhan: FC = () => {
    const { setBreakCrumb, setDSMenu, setEventKey, setIsLoading } = useContext(AppContext);
    let [isPrint, setIsPrint] = useState<boolean>(false);
    let [isPay, setIsPay] = useState<{ boolean: boolean, page: number, pageSize: number}>( { boolean: false, page: 0, pageSize: 0});
    const [thongTinBenhNhan, setThongTinBenhNhan] = useState<any>(initialValuesTiepDon);
    const [DSDichVu, setDSDichVu] = useState<any[]>([]);
    const [openDSBenhNhan, setOpenDSDSBenhNhan] = useState(false);
    const [orderMain, setOrderMain] = useState<object | null>(null);
    const currentUser = localStorageItem.get(KEY_LOCALSTORAGE.CURRENT_USER);

    const intl = useIntl();
    let breakCrumb = [
        { text: intl.formatMessage({ id: 'MENU.RECEIVE' }), url: '' },
        { text: intl.formatMessage({ id: 'MENU.RECEIVE' }), url: '/receive' },
    ];

    useEffect(() => {
        setBreakCrumb(breakCrumb);
        setDSMenu(danhSachMenu);
        return () => {
            setDSMenu([]);
            setBreakCrumb([]);
        };
    }, []);

    const handleAddTab = (eventKey: string) => {
        let data = localStorageItem.get(KEY_DS_TAB_TIEP_NHAN) ? localStorageItem.get(KEY_DS_TAB_TIEP_NHAN) : [];
        if (!data.includes(eventKey)) {
            data.push(eventKey);
        }
        data.sort((a: string, b: string) => a > b ? 1 : -1);
        localStorageItem.set(KEY_DS_TAB_TIEP_NHAN, data)
        setEventKey(eventKey)
    }

    let validationSchema = Yup.object({
        patient: Yup.object({
            person: Yup.object({
                fullName: Yup.string()
                    .required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })).nullable()
                    .matches(REGEX.TEN, intl.formatMessage({ id: 'VALIDATE.TEN' }))
                    .max(100, "Độ dài tối đa 100 ký tự!"),
                dobDay: Yup.number().typeError("Vui lòng nhập số")
                    .nullable()
                    .min(1, 'Ngày không hợp lệ')
                    .max(31, 'Ngày không hợp lệ')
                    .test('is-valid-date', 'Ngày không hợp lệ', function (value) {
                        const { dobDay, dobMonth, dobYear } = this.parent;
                        if (!dobMonth || (dobMonth && dobYear)) return true;
                        return validateNgay(dobDay, dobMonth, dobYear); 
                    }),
                dobMonth: Yup.number().typeError("Vui lòng nhập số")
                    .nullable()
                    .min(1, 'Tháng không hợp lệ')
                    .max(12, 'Tháng không hợp lệ')
                    .test('is-valid-month', 'Ngày không hợp lệ', function (value) {
                        const { dobYear } = this.parent;
                        if (!dobYear) return true;     
                        return true;
                    }),
                dobYear: Yup.number().typeError("Vui lòng nhập số")
                    .required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })).nullable()
                    .min(1900, 'Năm lớn hơn hoặc bằng 1900')
                    .max(new Date().getFullYear(), 'Năm nhỏ hơn hoặc bằng hiện tại')
                    .test('is-valid-year', 'Ngày sinh không được lớn hơn ngày hiện tại', function (value) {
                        const { dobDay, dobMonth, dobYear } = this.parent;
                        if (!dobMonth) return true; 
                        if (dobDay) {
                            const inputDate = new Date(dobYear, dobMonth - 1, dobDay);
                            const today = new Date();
                            return inputDate <= today;
                        }
    
                        const today = new Date();
                        if (dobYear > today.getFullYear() || (dobYear === today.getFullYear() && dobMonth && dobMonth > today.getMonth() + 1)) {
                            return false;  
                        }
                        return true;
                    }),
                idNumber: Yup.string().nullable().test(
                    'valid-idNumber',
                    'Số CCCD hoặc Hộ chiếu không hợp lệ',
                    (value: any) =>
                      /^[0-9]{9}$/.test(value) ||  
                      /^[0-9]{12}$/.test(value) || 
                      /^[A-Z][0-9]{7}$/.test(value) || !value
                ),
                phoneNumber: Yup.string().length(10,'Số điện thoại có độ dài 10 ký tự')
            }),
            code: Yup.string().length(8,'Mã bệnh nhân có độ dài 8 ký tự'),
        }),
        visit: Yup.object({
            visitObjectType: Yup.object().required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })).nullable(),
            dept: Yup.object().required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })).nullable(),
            service: Yup.object().required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })).nullable(),
        }),
        insurance: Yup.object().shape({    
            insFromDay: daySchema,
            insFromMonth: monthSchema,
            insFromYear: yearSchema,

            insToDay: daySchema,
            insToMonth: monthSchema,
            insToYear: yearSchema,

            insNgaymienchitraDay: daySchema,
            insNgaymienchitraMonth: monthSchema,
            insNgaymienchitraYear: yearSchema,

            insNgaydu5namDay: daySchema,
            insNgaydu5namMonth: monthSchema,    
            insNgaydu5namYear: yearSchema,
        })
    });

    const convertDataBenhNhan = (data: any): ITiepDon => {
        const convertToObject = (field: string, data: any) => {
            if (!data) {
                return null;
            };
            return (data[field + "Id"] && data[field + "Name"]) ? {
                id: data[field + "Id"],
                name: data[field + "Name"],
                code: data[field + "Code"],
            } : null;
        };
        return {
            ...data,
            patient: {
                ...data?.patient,
                person: {
                    ...data.patient.person,
                    dobDay: data?.patient?.dobDay 
                        ? `${data?.patient?.dobDay}`.padStart(2, '0') 
                        : null,
                    dobMonth: data?.patient?.dobMonth 
                        ? `${data?.patient?.dobMonth}`.padStart(2, '0') 
                        : null,
                    dobYear: data?.patient?.dobYear,
                    fullName: data?.patient?.personName,
                    phoneNumber: data?.patient?.phoneNumber || '',
                    idNumber: data?.patient?.idNumber,
                    gender: { name: data?.patient?.gender },
                    ccountry: convertToObject("country", data?.patient),
                },
                ethnicity: convertToObject("ethnicity", data?.patient),
                province: convertToObject("province", data?.patient),
                district: convertToObject("district", data?.patient),
                commune: convertToObject("commune", data?.patient),
                occupation: convertToObject("occupation", data?.patient),
                referrer: {
                    code: data?.patient?.referrerCode || "",
                    name: data?.patient?.referrerName || "",
                }
            },
            visit: {
                ...data?.visit,
                visitObjectType: convertToObject("visitObjectType", data?.visit) || data?.visit?.visitObjectType,
                visitReason: data?.visit?.visitReasonName,
                visitType: convertToObject("visitType", data?.visit),
                service: convertToObject("termSvc", data?.visit),
                dept: convertToObject("dept", data?.visit),
                attributes: {
                    examLevel: {
                        id: data?.visit?.attributes?.examLevel,
                        name: data?.visit?.attributes?.examLevelName,
                        code: data?.visit?.attributes?.examLevelCode,
                    }
                }
            },
            orders: data?.orders?.filter((order: any) => !order?.fulfillDeptId),
            initialDetailValues: data,
        }
    };

    const handleFormSubmit = async (values: ITiepDon, { setValues }: FormikHelpers<ITiepDon>) => {
        const currentDate = new Date();
        let patientValues = values?.patient;
        let visitValues = values?.visit;
        let ordersValues = values?.orders?.map((item: any) => {
            return {
                ...item,
                termId: item?.termId || item?.id,
                id: item?.termId ? item?.id : null
            };
        }) || [];
        let personValues = patientValues?.person;
        let insuranceValues = values?.insurance;
        let ordersKb = orderMain && typeof orderMain ==='object'
            ? {
            ...orderMain,
            termId: visitValues?.service?.id,
            fulfillDeptId: visitValues?.dept?.id || "",
            fulfillDeptName: visitValues?.dept?.name || "",
        } : {
            termId: visitValues?.service?.id,
            fulfillDeptId: visitValues?.dept?.id || "",
            fulfillDeptName: visitValues?.dept?.name || "",
        }
        let arr_orders = ordersValues.concat([ordersKb]);

        let isBHYT = CODE_DOI_TUONG.BAO_HIEM === visitValues?.visitObjectType?.code
        let userInfo = {
            indicationPerson: currentUser?.name,
            requestDeptName: currentUser?.departments?.[0]?.name || null
        };
        const submitData = {
            ...userInfo,
            patient: {
                person: {
                    ...personValues,
                    ccountry: null,
                    country: personValues?.ccountry?.code || "",
                    gender: personValues?.gender?.name || "",
                    dobDay: Number(personValues?.dobDay) || "",
                    dobMonth: Number(personValues?.dobMonth) || "",
                    dobYear: Number(personValues?.dobYear) || "",
                    phoneNumber: personValues?.phoneNumber || null,
                },
                personName: personValues?.fullName || "",
                allergyStatus: patientValues?.allergyStatus || "",
                phoneNumber: personValues?.phoneNumber || null,
                idNumber: personValues?.idNumber || "",

                gender: personValues?.gender?.name || "",
                genderCode: personValues?.gender?.code || "",

                ethnicityId: patientValues?.ethnicity?.id || "",
                ethnicityName: patientValues?.ethnicity?.name || "",
                ethnicityCode: patientValues?.ethnicity?.code || "",

                referrerCode: patientValues?.referrer?.code || "",
                referrerName: patientValues?.referrer?.name || "",
                address: patientValues?.address || "",
                administrativeUnit: patientValues?.administrativeUnit || "",

                provinceId: patientValues?.province?.id || "",
                provinceName: patientValues?.province?.name || "",
                provinceCode: patientValues?.province?.code || "",

                districtId: patientValues?.district?.id || "",
                districtName: patientValues?.district?.name || "",
                districtCode: patientValues?.district?.code || "",

                communeId: patientValues?.commune?.id || "",
                communeName: patientValues?.commune?.name || "",
                communeCode: patientValues?.commune?.code || "",

                occupationId: patientValues?.occupation?.id || "",
                occupationName: patientValues?.occupation?.name || "",
                occupationCode: patientValues?.occupation?.code || "",

                appointmentDate: formatDateDTO(patientValues?.appointmentDate || currentDate),
                deleted: patientValues?.deleted,
                deletedBy: patientValues?.deletedBy,
                deletedDate: patientValues?.deletedDate,
                deletedReason: patientValues?.deletedReason,
                id: patientValues?.id || "",
            },
            visit: {
                visitObjectTypeId: visitValues?.visitObjectType?.id || "",
                visitObjectTypeName: visitValues?.visitObjectType?.name || "",
                visitObjectTypeCode: visitValues?.visitObjectType?.code || "",

                visitTypeId: visitValues?.visitType?.id || "",
                visitTypeName: visitValues?.visitType?.name || "",
                visitTypeCode: visitValues?.visitType?.code || "",

                visitReasonName: visitValues?.visitReason || "",

                termSvcId: visitValues?.service?.id || "",
                termSvcName: visitValues?.service?.name || "",
                termSvcCode: visitValues?.service?.code || "",

                deptId: visitValues?.dept?.id || "",
                deptName: visitValues?.dept?.name || "",
                deptCode: visitValues?.dept?.code || "",

                attributes: {
                    ...visitValues?.attributes,
                    examLevel: visitValues?.attributes?.examLevel?.id,
                    examLevelName: visitValues?.attributes?.examLevel?.name,
                    examLevelCode: visitValues?.attributes?.examLevel?.code,
                },
                sortWeight: visitValues?.sortWeight,
            },
            orders: arr_orders,
            ...(isBHYT && {
                insurance: {
                    insCode: `${insuranceValues?.insCodeDoiTuong || ''}${insuranceValues?.insCodeMucHuong || ''}${insuranceValues?.insCodeNoiPhatHanh || ''}${insuranceValues?.insCodeMaBhxh || ''}`,
                    insLevelCode: visitValues?.attributes?.examLevel?.code,
                    insLevelName: visitValues?.attributes?.examLevel?.name,
                    insKcbbdCode: insuranceValues?.insKcbbdCode,
                    insKcbbdName: insuranceValues?.insKcbbdName,
                    insFromYear: !insuranceValues?.insFromYear ? null : Number(insuranceValues?.insFromYear),
                    insFromMonth: !insuranceValues?.insFromMonth ? null : Number(insuranceValues?.insFromMonth),
                    insFromDay: !insuranceValues?.insFromDay ? null : Number(insuranceValues?.insFromDay),
                    insToYear: !insuranceValues?.insToYear ? null : Number(insuranceValues?.insToYear),
                    insToMonth: !insuranceValues?.insToMonth ? null : Number(insuranceValues?.insToMonth),
                    insToDay: !insuranceValues?.insToDay ? null : Number(insuranceValues?.insToDay),
                    insNgaydu5namYear: !insuranceValues?.insNgaydu5namYear ? null : Number(insuranceValues?.insNgaydu5namYear),
                    insNgaydu5namMonth: !insuranceValues?.insNgaydu5namMonth ? null : Number(insuranceValues?.insNgaydu5namMonth),
                    insNgaydu5namDay: !insuranceValues?.insNgaydu5namDay ? null : Number(insuranceValues?.insNgaydu5namDay),
                    insNgaymienchitraYear: !insuranceValues?.insNgaymienchitraYear ? null : Number(insuranceValues?.insNgaymienchitraYear),
                    insNgaymienchitraMonth: !insuranceValues?.insNgaymienchitraMonth ? null : Number(insuranceValues?.insNgaymienchitraMonth),
                    insNgaymienchitraDay: !insuranceValues?.insNgaymienchitraDay ? null : Number(insuranceValues?.insNgaymienchitraDay),
                    insCoveragePercentage: !insuranceValues?.insCoveragePercentage ? null : Number(insuranceValues?.insCoveragePercentage),
                    //
                    ptName: personValues?.fullName,
                    ptBodDay: !personValues?.dobDay ? null : Number(personValues?.dobDay),
                    ptBodMonth: !personValues?.dobMonth ? null : Number(personValues?.dobMonth),
                    ptBodYear: !personValues?.dobYear ? null : Number(personValues?.dobYear),
                    ptGender: personValues?.gender?.name || "",
                    ptGenderName: personValues?.gender?.name || "",
                    insAddress: patientValues?.administrativeUnit || "",
                    id: insuranceValues?.id,
                    isUpdateCard: !visitValues?.id
						? false
						: checkInsuranceInfoChange(
								values?.initialDetailValues?.insurance,
								insuranceValues
						  ),
                    
                }
            }),
            ...(values?.vitalSigns && {
                vitalSigns: values?.vitalSigns
            }),
            ...(values?.additionalForms && {
                additionalForms: {
                    ...values?.additionalForms,
                    ...values?.familyRecords
                }
            })
        };
        
        try {
            setIsLoading(true);
            let { data } = (patientValues?.id && visitValues?.id)
                ? await updateBenhNhan({
                    ...submitData,
                    patient: {
                        ...patientValues,
                        ...submitData?.patient,
                        isUpdatePerson: true,
                        id: patientValues?.id
                    },
                    visit: {
                        ...visitValues,
                        ...submitData?.visit,
                        id: visitValues?.id
                    },
                    orders: [...submitData?.orders],
                    ...(isBHYT && {
                        insurance: {
                            ...insuranceValues,
                            ...submitData?.insurance,
                            id: insuranceValues?.id
                        }
                    }),
                    encounter: values?.encounter ? values?.encounter : null
                })
                : await addBenhNhan(submitData);
            if (CODE.SUCCESS === data?.code) {
                toast.success(((patientValues?.id && visitValues?.id) ? "Cập nhật" : "Thêm") + " bệnh nhân thành công");
                let dataConverted = convertDataBenhNhan(data?.data);
                let orderMain = data?.data?.orders?.find((order: any) => order?.fulfillDeptId);
                if (orderMain) {
                    setOrderMain(orderMain);
                }
                setValues(dataConverted);
                setThongTinBenhNhan({
                    ...thongTinBenhNhan,
                    isChangeThongTinBenhNhan: !thongTinBenhNhan?.isChangeThongTinBenhNhan
                });
            } else {
                let errorMesage = data?.data?.[0]?.errorMessage || "Xảy ra lỗi, vui lòng thử lại!";
                toast.warning(errorMesage);
            }
        } catch (error) {
            toast.warning("Xảy ra lỗi, vui lòng thử lại!");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleClosePrint = () => {
        setIsPrint(false)
    };

    const checkInsuranceInfoChange = (initialValue: any, currentValue: any) => {
        if (!initialValue || !currentValue) {
            return false;
        }

        const FIELD_CHECK = [
            'insCodeDoiTuong',
            'insCodeMucHuong',
            'insCodeNoiPhatHanh',
            'insCodeMaBhxh',
            'insFromYear',
            'insFromMonth',
            'insFromDay',
            'insToYear',
            'insToMonth',
            'insToDay',
        ];

        return FIELD_CHECK.some((field: string) => initialValue[field] !== currentValue[field]);
    };

    return (
        <Formik<ITiepDon>
            initialValues={{ ...initialValuesTiepDon }}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
        >
            <PhanHeTiepNhanContext.Provider
                value={{
                    thongTinBenhNhan, setThongTinBenhNhan,
                    isPrint, setIsPrint,
                    handleAddTab,
                    DSDichVu, setDSDichVu,
                    isPay, setIsPay,
                    orderMain, setOrderMain,
                    convertDataBenhNhan
                }}
            >
                <div className="reception-list spaces mt-6">
                    <div className="reception__header spaces pt-3 pb-6 border-bottom">
                        <Button className="btn-blue-outlined-custom ms-4 spaces h-29"
                            onClick={() => setOpenDSDSBenhNhan(true)}
                        ><i className="bi bi-people icon-call color-blue-custom fs-4"></i><span>Danh sách BN</span></Button>
                    </div>
                    <TiepNhan
                        openDSBenhNhan={openDSBenhNhan}
                        handleCloseDSBenhNhan={() => setOpenDSDSBenhNhan(false)}
                    />
                </div>
                {isPrint &&
                    <ComponentPrint
                        open={isPrint}
                        size="sm"
                        title="Phiếu tiếp đón"
                        handleClose={handleClosePrint}
                        data={convertDataTiepNhan({...thongTinBenhNhan, DSDichVu: DSDichVu})}
                        template={phieuTiepNhan}
                    />
                }
            </PhanHeTiepNhanContext.Provider>
        </Formik>
    )
}

export default PhanHeTiepNhan;
